import { assign, createMachine, Interpreter } from 'xstate-v4'
import { assertEventType } from '~/utility/xstate/assertEvent'
import { UserBooking, UserBookingSummary } from '../user-bookings/schemas'
import { UserNoTokenCanAdd } from './canAddCheckers/UserNoTokenCanAdd'
import { GuestBookingBooking } from './schema'
import { ReviewFormData } from './types'
import { GuestBookingRepositoryFetch } from './GuestBookingRepositoryFetch'
import { UserBookingsRepositoryFetch } from '../user-bookings/UserBookingsRepositoryFetch'
import { AddReviewRepositoryFetch } from './AddReviewRepositoryFetch'
import {
  reportApiError,
  reportAppError,
} from '~/utility/sentry/apiErrorReporter'
import { AddReviewFetchError, AddReviewValidationError } from './AddReviewError'

interface ReviewContext {
  langCode: string
  booking: GuestBookingBooking | UserBooking | null
  campsiteSlug: string
  canAdd: boolean
  checking: boolean
  error: string
  errors?: object
  isUser: boolean
  token?: string
  reviewId?: string
  reviewData?: ReviewFormData
}

function getContextInitial(langCode: string = 'en-gb') {
  return {
    langCode,
    booking: null,
    campsiteSlug: '',
    canAdd: false,
    checking: false,
    error: '',
    errors: undefined,
    isUser: false,
    token: '',
    reviewId: undefined,
    reviewData: undefined,
  }
}

interface CheckUserCanAdd {
  type: 'CHECK_USER_CAN_ADD'
  campsiteSlug: string
  token?: string
}

interface CheckGuestCanAdd {
  type: 'CHECK_GUEST_CAN_ADD'
  campsiteSlug: string
  token: string
}

export interface SubmitForm {
  type: 'SUBMIT_REVIEW'
  campsiteSlug: string
  reviewData: ReviewFormData
  token?: string
}

export type ReviewEvents =
  | CheckUserCanAdd
  | CheckGuestCanAdd
  | SubmitForm
  | { type: 'done.invoke.getUserBookings'; data: UserBookingSummary[] }

export function returnAddReviewStateMachine(langCode: string = 'en-gb') {
  return createMachine<ReviewContext, ReviewEvents>({
    /** @xstate-layout N4IgpgJg5mDOIC5QEMIQEpgG4EswHcBiAZQFUAhAWQEkAVAfXQFEA1apgdQG0AGAXUSgADgHtYOAC44RAO0EgAHogC0AZgDsqgHQ8ATJoAsGgKwAaEAE9E6g1uMAOXT1UBGXcYC+H86gzY8+Fo4EAA2YIQAwgASTBEA0vQA4qRMxAwRAIIAcvQZACJ5vAJIIKLiUrLySgg85lY1Xj5omLgEQaHh0bEJpMRM6PSZOfmF-PJlktJyJdW1log8jSC+LQFaAMYAFmDrANY4MlCksGAAThHIMhloTKenIqeECrASyBJgWsgAZu+nABSqHhAgCUhBW-jaWx2+0OxzOFyuNzuDyK4zEk0qMxURgAbFoAJzqXQ44w49x1RDufE6RzONyebzLZoQwJQvYHKCJACucAkCOuEEIEFkHwOWBEuw+4NarO27MO3N5-LQCDFInWbymRVRJQmFWmoFmFIajOlazZMM5PJeysFZ3upy0QhCby+DwAtp9mTKNnLLYqbZcBaqZOKNfrtWNdej9VUFsbFqbvea-RyA3yg2hCPaHk6XRI3adPWbIamFdaM4iICGw5rZNqXMVhDGpnGagmliXZdC0xXbdnkY7na6PV6-D6Lb2lZnq2rw1r+FxdE3Si3MYb4-MTU1xyme+Xp1Xbg6ni83lKfmcAUCeKCu7791bDwLjyio83yq2sQhlLiCUSSTJMwt1cdQ7FpVx3E7ZM2lgLkACN3UkKRDlPV53k+S9-kBW8wRgwI4MQ5COR1D8MQNRREBcHg8R4fETGNSC7HUex6Wg3dYIQpCJBQqBX0eZ50IvX5rxBPCOIIrjiMOfjSNXT910on8-0JYlSXJED1FoiC2KWGQRAgOB5C7NEFIo6plBsXQtHsIEWPpY1lHsMCGR3VY2mCMJTPItsDGMLRdBcAx7CMdRgPqEwaScSDXKZCSH3lI4TnOGdvNjb81BcbRdHsYwXHU8LKQcKK6SgpN4snWFkttfi0q-DcfxxXKtBxLKcocrd3HsEqYvY9zu0S9NbTqxSLNcbLcvyoDGJcPEHGi3S3JZBL-T7GdaujMy22UHF8VsWyeHsjSItm8CFrKpafUI7jeJG8yVH0HgtDC1R8Ro47EEingdIuuL+q0a7pL4wc7u2nEeFsTRHFYj6EC+n7YvvHNTngTafIy1rIda9QXGcwqEBcfFqXm0rEfwsdIFBjHVG0exVHO-GjG0Eneq8DwgA */
    id: 'addReview',
    context: getContextInitial(langCode),
    initial: 'idle',
    states: {
      idle: {
        entry: assign((ctx) => ({
          ...ctx,
          checking: false,
        })),
        on: {
          SUBMIT_REVIEW: {
            target: 'submitting',
            actions: [
              assign((_ctx, event) => {
                assertEventType(event, 'SUBMIT_REVIEW')
                const { campsiteSlug, token, reviewData } = event
                return {
                  campsiteSlug,
                  token,
                  reviewData,
                }
              }),
            ],
          },
          CHECK_GUEST_CAN_ADD: {
            target: 'checkingGuestCanAdd',
            actions: [
              assign({
                checking: (_) => true,
                isUser: (_) => false,
                token: (_ctx, event) => {
                  assertEventType(event, 'CHECK_GUEST_CAN_ADD')
                  return event.token
                },
              }),
            ],
          },
          CHECK_USER_CAN_ADD: {
            target: 'checkingUserCanAdd',
            actions: [
              assign({
                campsiteSlug: (_ctx, event) => {
                  assertEventType(event, 'CHECK_USER_CAN_ADD')
                  return event.campsiteSlug
                },
                checking: (_) => true,
                isUser: (_) => true,
                token: (_ctx, event) => {
                  assertEventType(event, 'CHECK_USER_CAN_ADD')
                  return event.token
                },
              }),
            ],
          },
        },
      },
      checkingUserCanAdd: {
        invoke: {
          src: 'getUserBookings',
          onDone: [{ target: 'idle', actions: ['checkUserNoTokenCanAdd'] }],
          onError: [
            {
              target: 'checkingUserCanAddError',
              actions: [
                'logError',
                assign({
                  error: (_, { data }) => data,
                }),
              ],
            },
          ],
        },
      },
      checkingUserCanAddError: {
        after: {
          3000: 'checkingUserCanAdd',
        },
      },
      checkingGuestCanAdd: {
        invoke: {
          src: 'checkGuestCanAdd',
          onDone: {
            target: 'idle',
            actions: assign({
              booking: (_, { data }) => data.booking,
              canAdd: (_) => true,
              error: (_) => '',
            }),
          },
          onError: [
            // if already added go to added state
            {
              target: 'added',
              cond: (_, { data }) => !!data.reviewId,
              actions: assign({
                reviewId: (_, { data }) => data.reviewId,
              }),
            },
            // if a user error display
            {
              target: 'errors',
              cond: (_, { data }) => !!data.error,
              actions: [
                assign({
                  booking: (_) => null,
                  canAdd: (_) => false,
                  error: (_, { data }) => data.error,
                }),
              ],
            },
            // retry any unexecpted errors and log them
            {
              target: 'checkingGuestCanAddError',
              actions: ['logError'],
            },
          ],
        },
      },
      checkingGuestCanAddError: {
        after: {
          3000: 'checkingGuestCanAdd',
        },
      },
      submitting: {
        invoke: {
          src: 'submitReview',
          onDone: [
            {
              cond: (_ctx, event) => !!event.data?.id,
              target: 'added',
              actions: assign({
                reviewId: (_, { data }) => data.id,
              }),
            },
            {
              target: 'idle',
            },
          ],
          onError: {
            target: 'submittingError',
            actions: [
              'logError',
              assign({
                error: (_ctx, { data }) =>
                  data?.data?.error || data?.message || 'Unknown error',
                errors: (_ctx, { data }) => data?.data?.errors || {},
              }),
            ],
          },
        },
      },
      submittingError: {
        always: [
          {
            target: 'idle',
            cond: (context) =>
              !!context.errors && Object.keys(context.errors).length > 0,
          },
        ],
        after: {
          3000: 'submitting',
        },
      },
      errors: {},
      added: {
        type: 'final',
      },
    },
  }).withConfig({
    services: {
      checkGuestCanAdd: async (ctx) => {
        if (!ctx.token) {
          throw new Error('token_required')
        }
        const repo = new GuestBookingRepositoryFetch()
        const response = await repo.getByToken(ctx.token, ctx.langCode)
        return response.booking
          ? Promise.resolve(response)
          : Promise.reject(
              new Error(
                `Failed to load guest booking: ${response.error || 'Booking not found'}`,
              ),
            )
      },
      getUserBookings: (ctx) => {
        const repo = new UserBookingsRepositoryFetch()
        return repo.readAll(ctx.langCode)
      },
      submitReview: (ctx) => {
        const { campsiteSlug, token, reviewData, langCode } = ctx
        if (!reviewData) {
          throw new Error('review_data_required')
        }
        const repo = new AddReviewRepositoryFetch()
        return repo.create(campsiteSlug, reviewData, token || '', langCode)
      },
    },
    actions: {
      checkUserNoTokenCanAdd: assign((context, event) => {
        assertEventType(event, 'done.invoke.getUserBookings')
        const { data } = event
        let booking: UserBooking | null = null
        let canAdd = true
        let error = ''
        try {
          booking = new UserNoTokenCanAdd().check(
            data as UserBooking[],
            context.campsiteSlug,
          )
        } catch (err) {
          canAdd = false
          error = (err as Error).message
        }
        return {
          booking,
          canAdd,
          error,
          token: booking ? booking.token : '',
        }
      }),
      logError: (ctx, event: any) => {
        // Handle structured error logging for our custom error types
        const err = event?.data || event
        if (
          err instanceof AddReviewFetchError ||
          err instanceof AddReviewValidationError
        ) {
          // Extract validation error details for better Sentry filtering
          let validationError = undefined
          if (err instanceof AddReviewValidationError) {
            // Extract the most specific validation error for tagging
            const serverErrors = err.serverErrors || {}
            const firstErrorKey = Object.keys(serverErrors)[0]
            if (firstErrorKey && serverErrors[firstErrorKey]?.[0]) {
              validationError = serverErrors[firstErrorKey][0]
            }
          }

          reportApiError(err, 'review submission', {
            // Machine-specific context
            campsiteSlug: ctx.campsiteSlug,
            isUser: ctx.isUser,
            canAdd: ctx.canAdd,
            // Review form context if available
            hasReviewData: !!ctx.reviewData,
            reviewDataKeys: ctx.reviewData ? Object.keys(ctx.reviewData) : [],
            // Add validation error for filtering/analysis
            validationError,
          })
        } else {
          // Fallback for unexpected errors
          reportAppError(err, 'review submission machine', {
            eventType: event?.type,
            campsiteSlug: ctx.campsiteSlug,
            isUser: ctx.isUser,
            canAdd: ctx.canAdd,
            hasToken: !!ctx.token,
            error: err?.message || event?.message || 'Unknown machine error',
          })
        }
      },
    },
  })
}

export type AddReviewStateMachineInterpreter = Interpreter<
  ReviewContext,
  object,
  ReviewEvents
>
